import axios from "axios";
import { getActiveWorkspaceId, setActiveWorkspaceId } from "@/lib/workspace";
import { mirror } from "@/lib/offline/mirror";
import { resolveGet } from "@/lib/offline/resolve";
import { canQueue, enqueue } from "@/lib/offline/queue";
import { getSessionToken } from "@/lib/authToken";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 60000 });

export { setActiveWorkspaceId };

const offlineError = (message) => {
  const error = new Error(message);
  error.offline = true;
  error.response = { status: 503, data: { detail: message } };
  return error;
};

api.interceptors.request.use((config) => {
  const workspaceId = getActiveWorkspaceId();
  if (workspaceId) {
    config.params = { ...(config.params || {}), workspace_id: workspaceId };
  }
  const token = getSessionToken();
  if (token) {
    if (!config.headers) config.headers = {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // signing in always needs the network — never divert it to the offline layer
  const signingIn = String(config.url || "").includes("/auth/session");
  if (!signingIn && typeof navigator !== "undefined" && navigator.onLine === false) {
    const error = new Error("offline");
    error.isOffline = true;
    error.config = config;
    throw error;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    if ((response.config?.method || "get").toLowerCase() === "get") {
      mirror(response.config, response.data);
    }
    return response;
  },
  async (error) => {
    const config = error.config;
    const networkDown =
      error.isOffline ||
      (!error.response &&
        (error.code === "ERR_NETWORK" ||
          error.code === "ECONNABORTED" ||
          error.message === "Network Error"));
    if (!config || !networkDown) return Promise.reject(error);

    const method = (config.method || "get").toLowerCase();
    const offlineResponse = (data, status) => ({
      data,
      status,
      statusText: "offline",
      headers: {},
      config,
      offline: true,
    });

    if (method === "get") {
      const cached = await resolveGet(config);
      if (cached !== undefined && cached !== null) return offlineResponse(cached, 200);
      return Promise.reject(
        offlineError("Not on this device yet — connect once so it can be saved for offline use"),
      );
    }

    if (canQueue(config)) {
      const data = await enqueue(config);
      window.dispatchEvent(new Event("offline-queued"));
      return offlineResponse(data, 202);
    }

    return Promise.reject(offlineError("You're offline — this one needs a connection"));
  },
);

export const errText = (e) =>
  e?.response?.data?.detail || e?.message || "Something went wrong";
